import type { DisplayToolRun } from "./types"

export type ReplyBlock =
  | { kind: "text"; text: string }
  | { kind: "tools"; tools: DisplayToolRun[] }

// A reply in the order it happened: text, the tools called at that point, more text.
// Tools saved before `at` existed go first, as they used to
export function interleave(content: string, tools: DisplayToolRun[] = []): ReplyBlock[] {
  const sorted = tools
    .map((t, i) => ({ t, i, at: Math.min(Math.max(t.at ?? 0, 0), content.length) }))
    .sort((a, b) => a.at - b.at || a.i - b.i)
  const blocks: ReplyBlock[] = []
  let pos = 0
  for (const { t, at } of sorted) {
    if (at > pos) {
      const text = content.slice(pos, at)
      if (text.trim()) blocks.push({ kind: "text", text })
      pos = at
    }
    const last = blocks[blocks.length - 1]
    if (last?.kind === "tools") last.tools.push(t)
    else blocks.push({ kind: "tools", tools: [t] })
  }
  const rest = content.slice(pos)
  if (rest.trim()) blocks.push({ kind: "text", text: rest })
  return blocks
}

// Applies `fn` to each stretch of text between tools, keeping every tool's position right
export function mapTextBetweenTools(content: string, tools: DisplayToolRun[], fn: (text: string) => string) {
  const cuts = [...new Set(tools.map(t => Math.min(Math.max(t.at ?? 0, 0), content.length)))].sort((a, b) => a - b)
  const moved = new Map<number, number>()
  let out = ""
  let pos = 0
  for (const cut of [...cuts, content.length]) {
    out += fn(content.slice(pos, cut))
    moved.set(cut, out.length)
    pos = cut
  }
  return {
    content: out,
    tools: tools.map(t => (t.at == null ? t : { ...t, at: moved.get(Math.min(Math.max(t.at, 0), content.length)) ?? t.at })),
  }
}
